import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Quote, Loader2, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getYoutubeId = (url = '') => {
  const m = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([\w-]{11})/);
  return m ? m[1] : null;
};

export default function TestimonialsPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/videos`);
      setVideos((res.data || []).filter((v) => v.category === 'temoignage'));
    } catch (e) {
      console.error('Failed to load testimonials:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <section className="section-padding bg-[#F9F8F6]" data-testid="testimonials-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <Quote className="text-[#4A6B48]" size={40} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-3"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Témoignages
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto">
              Les paroles authentiques des bénéficiaires, partenaires et acteurs de terrain
              qui font vivre les îlots de développement intégral.
            </p>
          </div>

          {loading && (
            <div className="flex justify-center py-12">
              <Loader2 className="animate-spin text-[#4A6B48]" size={32} />
            </div>
          )}

          {!loading && videos.length === 0 && (
            <div className="text-center py-12" data-testid="testimonials-empty">
              <p className="text-[#5C665A] mb-3">Aucun témoignage publié pour le moment.</p>
              <p className="text-xs text-[#5C665A] italic">
                Les témoignages seront publiés au fil des activités de CRESA.
              </p>
            </div>
          )}

          {!loading && videos.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {videos.map((v) => {
                const ytId = getYoutubeId(v.youtube_url || v.url || '');
                const thumb = ytId
                  ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`
                  : (v.thumbnail_url || '');
                return (
                  <article key={v.video_id || v.id} className="card overflow-hidden p-0"
                    data-testid={`testimonial-card-${v.video_id || v.id}`}>
                    <button onClick={() => setActive(v)} className="block w-full text-left">
                      <div className="relative aspect-video bg-[#2E332D]">
                        {thumb ? (
                          <img src={thumb} alt={v.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-white">
                            <Quote size={40} />
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/10 transition-colors">
                          <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <Play className="text-[#4A6B48] ml-1" size={26} />
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h2 className="font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                          {v.title}
                        </h2>
                        {v.description && (
                          <p className="text-xs text-[#5C665A] mt-1 line-clamp-2">{v.description}</p>
                        )}
                      </div>
                    </button>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Inline video player modal */}
      {active && (() => {
        const ytId = getYoutubeId(active.youtube_url || active.url || '');
        let playerNode;
        if (ytId) {
          playerNode = (
            <iframe
              title={active.title}
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          );
        } else if (active.video_url) {
          playerNode = <video src={active.video_url} controls autoPlay playsInline preload="metadata" controlsList="nodownload noremoteplayback" onContextMenu={(e) => e.preventDefault()} className="w-full h-full" />;
        } else {
          playerNode = (
            <div className="flex items-center justify-center h-full text-white">
              Vidéo indisponible
            </div>
          );
        }
        return (
          <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }} onClick={() => setActive(null)} data-testid="testimonial-player">
            <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <div className="aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
                {playerNode}
              </div>
              <button onClick={() => setActive(null)}
                data-testid="close-testimonial"
                className="mt-3 text-white text-sm font-bold underline">
                Fermer
              </button>
            </div>
          </div>
        );
      })()}

      <Footer />
    </div>
  );
}
